<template>
  <div class="recruiter-kpi-dashboard">
    <TableList
      v-rect-loading="loading"
      class="recruiter-kpi-list"
      ref="tableList"
      noAction
      showSearchFilter
      :title="$t(`${i18nPrefix}.recruiterKpiDashboard.title`)"
      :listData="listData"
      :tableColumns="tableColumns"
      :showCreateBtn="false"
      :pagination="pagination"
      :defaultSort="defaultSort"
      @pagination-change="handlePaginationChange"
      @sort-change="handleSortChange"
    >
    <template #search-filter>
      <div class="company-filter">
        <span v-if="!hiddenFilterTitle" class="text">{{ $t(`${i18nPrefix}.label.companyName`) }}</span>
        <span class="company-select">
          <el-select v-model="selectCompany" filterable @change="handleCompanySelect">
            <el-option
              v-for="item in companyList"
              :key="item.id"
              :label="isEn() ? item.name : item.cn_name"
              :style="{'padding-left':item.level*15+'px'}"
              :value="item.id"
            >
            </el-option>
          </el-select>
        </span>
      </div>
      <el-date-picker
        v-model="startAndEndDate"
        class="start-end-date-picker"
        type="daterange"
        range-separator="-"
        value-format="yyyy-MM-dd"
        align="right"
        unlink-panels
        :clearable="false"
        :start-placeholder="$t(`${i18nPrefix}.label.startDate`)"
        :end-placeholder="$t(`${i18nPrefix}.label.endDate`)"
        :picker-options="pickerOptions"
        @change="handleDateChange"
      >
      </el-date-picker>
      <div class="company-hr-filter">
        <el-cascader
          v-model="selectedHrList"
          ref="hrCascaderRef"
          class="hr-filter"
          clearable
          filterable
          medium
          collapse-tags
          popper-class="hr-filter-popper"
          :size="cascaderSize"
          :options="searchHRFilterGroups"
          :props="hrFilterProps"
          :placeholder="$t(`${i18nPrefix}.label.selectHr`)"
          :filter-method="handleFilterHr"
          @change="handleHrChange"
        >
          <template slot-scope="{ node, data }">
            <div class="filter-node">
              <span class="node-name">{{ data.name }}</span>
              <span v-if="!node.isLeaf" class="node-count"> ({{ data.children.length }}) </span>
              <span v-if="(!node.isLeaf && data.id !== 'all') || (node.isLeaf && node.parent.value !== 'all')" class="delete-group"><i class="delete-icon el-icon-delete" @click.stop="deleteSearchTemplate(node, data)"></i></span>
            </div>
          </template>
        </el-cascader>
        <el-button v-if="selectedHrList.length > 0" class="blue-btn save-btn" @click="handleSaveHrFilter">{{ $t(`${i18nPrefix}.btn.save`) }}</el-button>
      </div>
    </template>
    <template #search-filter-btn>
      <el-button v-rect-loading="exportKpiStatus.loading"  class="blue-btn export-btn" @click="handleExport">{{ $t(`${i18nPrefix}.btn.export`) }}</el-button>
    </template>
    <template #userName="{column}">
      <span class="user-name-title">{{ column.label }}</span>
      <el-dropdown trigger="click" placement="bottom-end" @visible-change="handleDropDownVisible">
        <span class="el-dropdown-link">
          <i class="iconfont icona-ApplicationConfiguration icon-config"></i>
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-checkbox class="checkbox-check-all" :indeterminate="isIndeterminate" v-model="checkAll" @change="handleCheckAllChange">{{  $t(`${i18nPrefix}.btn.checkAll`) }}</el-checkbox>
            <el-checkbox-group v-model="showColumns" class="config-checkbox-group" @change="handleCheckedColumnChange">
              <el-checkbox class="config-checkbox-item"
                v-for="(item, index) in checkboxOptions"
                :key="index"
                :label="item.dataKey"
                >
                {{ item.label }}
              </el-checkbox>
            </el-checkbox-group>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </template>
    </TableList>
    <SaveSearchDialog
      v-loading="loading"
      ref="saveSearchDialog"
      :searchTemplateList="searchTemplateList"
      :searchTemplate="searchTemplate"
      :titleI18nKey="`${i18nPrefix}.message.saveGroupTitle`"
      :saveTipI18nKey="`${i18nPrefix}.message.saveGroupTip`"
      :saveContentI18nKey="`${i18nPrefix}.message.saveGroupContent`"
      :updateContentI18nKey="`${i18nPrefix}.message.updateGroupContent`"
      @save="handleSaveSearchSave"
    >
    </SaveSearchDialog>
    <ConfirmDialog
      v-if="confirmDialog.show"
      :content="confirmDialog.content"
      :show.sync="confirmDialog.show"
      :title="confirmDialog.title"
      :loading="confirmDialog.loading"
      @confirm="handleConfirmDelete"
      @cancel="handleCancelDelete"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { getI18nPropertyValue, isEn } from '@/i18n'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import ConfirmDialog from '@/components/dialog/confirmDialog.vue'
import SaveSearchDialog from '@/views/adminNew/applicantList/saveSearchDialog.vue'
import { AsyncTask } from '@/utils/common'
import moment from 'moment'

export default {
  components: {
    TableList,
    SaveSearchDialog,
    ConfirmDialog
  },
  props: {
    notSaveQuery: {
      type: Boolean,
      default: false
    },
    hiddenFilterTitle: {
      type: Boolean,
      default: false
    },
    cascaderSize: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.chartReport',
      hrFilterProps: {
        multiple: true,
        value: 'id',
        label: 'name'
      },
      isEn,
      loading: false,
      startAndEndDate: [moment().add(-7, 'days').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
      listData: [],
      companyList: [],
      selectCompany: null,
      searchParams: {
        start_date: moment().add(-7, 'days').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
        page: 1,
        page_size: 10,
        company_id: null,
        order: null,
        select_hr_ids: null
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      exportKpiStatus: {
        loading: false,
        task: null,
        finished: false
      },
      hrList: [],
      selectedHrList: [],
      hrSearchGroupList: [],
      searchTemplate: null,
      searchTemplateList: [],
      deletedTemplate: null,
      confirmDialog: {
        show: false,
        content: '',
        title: '',
        loading: false
      },
      defaultSort: {
        prop: null,
        order: null
      },
      widgetChannel: {},
      showColumns: [],
      checkAll: false,
      isIndeterminate: true
    }
  },
  computed: {
    tableColumns () {
      const vm = this
      const widgetChannel = _.map(this.widgetChannel, (item) => {
        return {
          get label () {
            return vm.$t(`${vm.i18nPrefix}.label.widgetResumeViewedCount`, { channel: getI18nPropertyValue(_.get(vm.widgetChannel, `channel_${item.id}`), 'cn_name', 'name') })
          },
          dataKey: `channel_${item.id}`,
          formatNumber: true,
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          width: '260px',
          align: 'center'
        }
      })
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.userName`),
          dataKey: 'user_name',
          headerSlotName: 'userName',
          width: '150px',
          fixed: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.resumeViewedCount`),
          dataKey: 'resume_viewed_count',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          width: '230px',
          align: 'center'
        },
        ...widgetChannel,
        {
          label: this.$t(`${this.i18nPrefix}.label.incomingResumeCount`),
          dataKey: 'incoming_resume_count',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          width: '180px',
          align: 'center'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.referralResumeCount`),
          dataKey: 'referral_resume_count',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          width: '180px',
          align: 'center'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.interviewResumeCount`),
          dataKey: 'interview_resume_count',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          width: '180px',
          align: 'center'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.offeredResumeCount`),
          dataKey: 'offered_resume_count',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          width: '180px',
          align: 'center'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.userUsageDuration`),
          dataKey: 'user_usage_duration',
          formatter: (value) => {
            const h = Math.floor(value / 60)
            const mm = value % 60
            let step = ''
            if (h) {
              step = this.$t(`${this.i18nPrefix}.label.hour`, { hour: h })
            }
            if (mm) {
              step += this.$t(`${this.i18nPrefix}.label.minute`, { minute: mm })
            }
            return step || 0
          },
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          width: '180px',
          align: 'center'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.activeDays`),
          dataKey: 'active_days',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          width: '180px',
          align: 'center'
        }
      ]
      return _.map(columns, item => ({
        ...item,
        get hidden () {
          return !_.includes(vm.showColumns, item.dataKey)
        }
      }))
    },
    checkboxOptions () {
      return _.map(this.tableColumns, column => {
        return {
          dataKey: column.dataKey,
          label: column.label
        }
      }).filter(column => column.dataKey !== 'user_name')
    },
    searchHRFilterGroups () {
      const list = [
        {
          name: this.$t(`${this.i18nPrefix}.label.selectAllHr`),
          id: 'all',
          children: this.hrList
        }
      ]
      return _.concat(list, this.hrSearchGroupList)
    },
    pickerOptions () {
      return {
        shortcuts: [
          {
            text: this.$t(`${this.i18nPrefix}.label.last7Days`),
            onClick (picker) {
              const end = moment().format('YYYY-MM-DD')
              const start = moment().add(-7, 'days').format('YYYY-MM-DD')
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: this.$t(`${this.i18nPrefix}.label.last30Days`),
            onClick (picker) {
              const end = moment().format('YYYY-MM-DD')
              const start = moment().add(-30, 'days').format('YYYY-MM-DD')
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: this.$t(`${this.i18nPrefix}.label.last90Days`),
            onClick (picker) {
              const end = moment().format('YYYY-MM-DD')
              const start = moment().add(-90, 'days').format('YYYY-MM-DD')
              picker.$emit('pick', [start, end])
            }
          }
        ]
      }
    }
  },
  created () {
    if (this.$route.query) {
      const start = this.$route.query.start_date
      if (start) {
        this.searchParams.start_date = start
      }
      const end = this.$route.query.end_date
      if (end) {
        this.searchParams.end_date = end
      }
      if (this.searchParams.start_date && this.searchParams.end_date) {
        this.startAndEndDate = [this.searchParams.start_date, this.searchParams.end_date]
      }
      this.searchParams.select_hr_ids = this.$route.query.select_hr_ids
      this.searchParams.company_id = this.$route.query.company_id
      this.searchParams.page_size = this.$route.query.page_size || 10
      this.searchParams.page = this.$route.query.page || 1
      this.searchParams.order = this.$route.query.order
      if (this.searchParams.order) {
        const order = this.searchParams.order.replace('-', '')
        const orderType = this.searchParams.order.startsWith('-') ? 'descending' : 'ascending'
        this.defaultSort = {
          prop: order,
          order: orderType
        }
      }
    }
    this.initData()
  },
  methods: {
    ...mapActions('chartReport', ['getRecruiterKpiReportData', 'createExportRecruiterKpiDataTask', 'getExportRecruiterKpiTaskStatus']),
    ...mapActions('company', ['getCompanyChildrenTree', 'getTeamMemberList']),
    ...mapActions('account', ['getAccountPageConfigList', 'createAccountPageConfig', 'updateAccountPageConfig', 'deleteAccountPageConfig']),
    async initData () {
      this.loading = true
      try {
        await this.loadCompanyList()
        await this.loadCompanyHrList()
      } finally {
        this.loading = false
      }
      Promise.all([this.loadRecruiterKpiData(), this.loadSearchTemplateList()])
    },
    async loadSearchTemplateList () {
      const resp = await this.getAccountPageConfigList({ page_type: 'RECRUITER_KPI_REPORT' })
      if (resp) {
        this.hrSearchGroupList = []
        this.searchTemplateList = _.chain(resp).map(pageConfig => {
          const search = pageConfig.config.search
          const companyId = search.company_id
          if (companyId === this.selectCompany) {
            return {
              id: pageConfig.id,
              name: pageConfig.config.name,
              search: pageConfig.config.search
            }
          }
        }).filter().value()

        _.forEach(this.searchTemplateList, item => {
          const search = item.search
          const selectedHrIds = search.select_hr_ids
          const hrList = _.filter(this.hrList, hr => _.includes(selectedHrIds, hr.id))
          this.hrSearchGroupList.push({
            name: item.name,
            id: item.id,
            isGroupFilter: true,
            children: hrList
          })
        })
      }
    },
    async loadRecruiterKpiData () {
      this.loading = true
      try {
        const hrIds = this.searchParams.select_hr_ids
        const params = { ...this.searchParams, select_hr_ids: (hrIds && hrIds.split(',')) || [] }
        params.end_date = moment(this.searchParams.end_date).endOf('day').format('YYYY-MM-DD HH:mm:ss')
        const resp = await this.getRecruiterKpiReportData(params)
        if (resp) {
          this.listData = resp.data
          this.pagination.total = resp.total_count
          this.widgetChannel = resp.widget_channel
        }
        await this.loadShowColumnList()
      } finally {
        this.loading = false
      }
    },
    async loadCompanyHrList () {
      const params = { companyId: this.selectCompany, is_all: true, exclude_disabled: true }
      const resp = await this.getTeamMemberList(params)
      if (resp) {
        this.hrList = _.orderBy(resp.results, hr => hr.name.toLowerCase(), 'asc')
        if (this.searchParams.select_hr_ids) {
          const selectHrIds = this.searchParams.select_hr_ids.split(',')
          this.selectedHrList = _.map(selectHrIds, hrId => {
            return ['all', hrId]
          })
        }
      }
    },
    async loadShowColumnList () {
      const resp = await this.getAccountPageConfigList({ page_type: 'RECRUITER_KPI_REPORT_SHOE_COLUMN' })
      if (resp.length > 0) {
        const respData = resp[0]
        this.configId = respData.id
        this.showColumns = respData.config
      } else {
        const showColumns = _.map(this.checkboxOptions, item => item.dataKey)
        showColumns.push('user_name')
        this.showColumns = showColumns
      }
      this.handleCheckedColumnChange(this.showColumns)
    },
    async saveShowColumnList () {
      if (this.configId) {
        this.updateAccountPageConfig({
          id: this.configId,
          config: this.showColumns
        })
      } else {
        const resp = await this.createAccountPageConfig({
          page_type: 'RECRUITER_KPI_REPORT_SHOE_COLUMN',
          config: this.showColumns
        })
        if (resp) {
          this.configId = resp.id
        }
      }
    },
    async loadCompanyList () {
      const companyTreeResp = await this.getCompanyChildrenTree()
      const currentCompany = {
        id: companyTreeResp.self.id,
        name: companyTreeResp.self.name,
        cn_name: companyTreeResp.self.cn_name,
        level: companyTreeResp.self.level
      }
      this.selectCompany = _.parseInt(this.searchParams.company_id) || currentCompany.id
      this.searchParams.company_id = this.selectCompany
      const companyList = []
      this.companyList = this.formatCompanyData(companyTreeResp, currentCompany, companyList)
    },
    handleSortChange (prop, order) {
      this.searchParams.order = order ? `${order}${prop}` : prop
      this.loadRecruiterKpiData()
    },
    async handleCompanySelect () {
      this.searchParams.company_id = this.selectCompany
      this.searchParams.select_hr_ids = null
      this.selectedHrList = []
      this.loadRecruiterKpiData()
      await this.loadCompanyHrList()
      await this.loadSearchTemplateList()
    },
    handleFilterHr (node, keywords) {
      return _.includes(node.label.toLowerCase(), keywords.toLowerCase())
    },
    handleHrChange () {
      this.searchParams.select_hr_ids = _.uniq(_.map(this.selectedHrList, item => item[1])).join(',')
      if (!this.searchDebounce) {
        this.searchDebounce = _.debounce(this.loadRecruiterKpiData, 1000)
      }
      this.searchDebounce()
    },
    handleSaveHrFilter () {
      this.$refs.saveSearchDialog.openDialog()
    },
    async handleSaveSearchSave (templateInfo, callback) {
      this.loading = true
      try {
        if (templateInfo.isEditMode) {
          const template = _.find(this.searchTemplateList, item => item.name === templateInfo.templateName)
          const search = template.search
          const hasSelectHrIds = search.select_hr_ids.split(',')
          const selectHrIds = _.map(this.selectedHrList, item => item[1])
          const newSelectHrIds = _.concat(hasSelectHrIds, selectHrIds)
          search.select_hr_ids = _.uniq(newSelectHrIds).join(',')
          const resp = await this.updateAccountPageConfig({
            id: template.id,
            config: {
              name: template.name,
              search: search
            }
          })
          if (resp) {
            this.$message({
              type: 'success',
              message: this.$t(`${this.i18nPrefix}.message.saveGroupSuccess`)
            })
            callback(templateInfo)
          }
        } else {
          const resp = await this.createAccountPageConfig({
            page_type: 'RECRUITER_KPI_REPORT',
            config: {
              name: templateInfo.templateName,
              search: this.searchParams
            }
          })
          if (resp) {
            this.$message({
              type: 'success',
              message: this.$t(`${this.i18nPrefix}.message.saveGroupSuccess`)
            })
            callback(templateInfo)
          }
        }
      } finally {
        this.loading = false
        this.loadSearchTemplateList()
      }
    },
    deleteSearchTemplate (node, searchTemplate) {
      this.confirmDialog.show = true
      this.confirmDialog.content = this.$t(`${this.i18nPrefix}.message.deleteFilterContent`)
      this.confirmDialog.title = this.$t(`${this.i18nPrefix}.message.deleteFilterTitle`)
      this.deletedTemplate = searchTemplate
      this.deletedNode = node
    },
    async handleConfirmDelete () {
      this.confirmDialog.loading = true
      const isGroup = this.deletedTemplate.isGroupFilter
      let resp
      try {
        if (isGroup) {
          resp = await this.deleteAccountPageConfig({
            id: this.deletedTemplate.id
          })
          this.searchTemplateList.splice(_.findIndex(this.searchTemplateList, item => item.id === this.deletedTemplate.id), 1)
          this.selectedHrList = []
        } else {
          const hrId = this.deletedTemplate.id
          this.selectedHrList = _.filter(this.selectedHrList, item => item[1] !== hrId)
          const searchParams = this.searchParams
          this.searchParams.select_hr_ids = _.map(this.selectedHrList, item => item[1]).join(',')
          const selectHrConfig = _.filter(this.deletedNode.parent.children, item => item.value !== hrId)
          searchParams.select_hr_ids = _.map(selectHrConfig, item => item.value).join(',')
          const parentTemplate = this.deletedNode.parent
          resp = await this.updateAccountPageConfig({
            id: parentTemplate.value,
            config: {
              name: parentTemplate.label,
              search: searchParams
            }
          })
        }
        if (resp) {
          this.$message({
            type: 'success',
            message: this.$t(`${this.i18nPrefix}.message.deleteSearchGroupSuccess`)
          })
        }
      } finally {
        this.loadSearchTemplateList()
        this.loadRecruiterKpiData()
        this.confirmDialog.loading = false
        this.confirmDialog.show = false
      }
    },
    handleCancelDelete () {
      this.confirmDialog.show = false
      this.deletedTemplate = null
      this.$message({
        type: 'info',
        message: this.$t(`${this.i18nPrefix}.message.cancelDeleteSearchGroup`)
      })
    },
    handlePaginationChange () {
      this.searchParams.page = this.pagination.page
      this.searchParams.page_size = this.pagination.pageSize
      this.loadRecruiterKpiData()
    },
    async handleDateChange () {
      if (this.startAndEndDate) {
        const start = this.startAndEndDate[0]
        const end = this.startAndEndDate[1]
        if (moment(end).diff(moment(start), 'days') > 90) {
          this.$message({
            type: 'warning',
            message: this.$t(`${this.i18nPrefix}.message.dateRangeTooLong`)
          })
          return
        }
        this.searchParams.start_date = null
        this.searchParams.end_date = null
        this.searchParams.start_date = this.startAndEndDate[0]
        this.searchParams.end_date = this.startAndEndDate[1]
      }
      await this.loadRecruiterKpiData()
    },
    handleExport () {
      const exportKpiStatus = this.exportKpiStatus
      if (exportKpiStatus.loading) {
        return
      }
      exportKpiStatus.loading = true
      exportKpiStatus.finished = false
      const hrIds = this.searchParams.select_hr_ids
      const params = { ...this.searchParams, select_hr_ids: (hrIds && hrIds.split(',')) || [] }
      params.end_date = moment(this.searchParams.end_date).endOf('day').format('YYYY-MM-DD HH:mm:ss')
      exportKpiStatus.task = new AsyncTask(() => this.createExportRecruiterKpiDataTask(params), (taskId) => this.getExportRecruiterKpiTaskStatus({
        task_id: taskId
      }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/chart-report/export-excel/recruiter-kpi-data/?task_id=${taskId}&download=1`)
        exportKpiStatus.loading = false
        exportKpiStatus.finished = false
        exportKpiStatus.task = null
      })
    },
    handleCheckAllChange (value) {
      const showColumns = _.map(this.checkboxOptions, item => item.dataKey)
      showColumns.push('user_name')
      this.showColumns = value ? showColumns : ['user_name']
      this.isIndeterminate = false
    },
    handleCheckedColumnChange (value) {
      const checkedCount = value.length
      this.checkAll = checkedCount === this.checkboxOptions.length + 1
      this.isIndeterminate = checkedCount > 1 && checkedCount < this.checkboxOptions.length + 1
    },
    handleDropDownVisible (value) {
      if (!value) {
        this.saveShowColumnList()
      }
    },
    formatCompanyData (data, company, companyList) {
      companyList.push(company)
      const allChildren = data.children
      const children = allChildren[company.id]
      if (children && children.length > 0) {
        for (const child of children) {
          this.formatCompanyData(data, child, companyList)
        }
      }
      return companyList
    }
  },
  watch: {
    searchParams: {
      handler () {
        if (!this.notSaveQuery) {
          const paramsList = []
          _.forEach(this.searchParams, (value, key) => {
            if (_.isNumber(value)) {
              paramsList.push(`${key}=${value}`)
            } else if (_.isString(value)) {
              paramsList.push(`${key}=${value}`)
            }
          })
          const params = paramsList.join('&')
          if (params) {
            const path = `${this.$route.path}?${params}`
            if (path !== this.$route.fullPath) {
              this.$router.push(path)
            }
          }
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.recruiter-kpi-dashboard {
  .recruiter-kpi-list {
    .company-filter {
      display: flex;
      align-items: center;
      margin-right: 20px;
      .text {
        margin-right: 10px;
      }
      .company-select {
        ::v-deep {
          .el-select {
            .el-input__inner {
              height: 40px;
              line-height: 40px;
            }
          }
        }
      }
    }
    .start-end-date-picker {
      width: 26%;
      border-radius: 4px;
      margin-right: 20px;
    }

    .company-hr-filter {
      margin-right: 42px;
      display: flex;
      align-items: center;
      .hr-filter {
        margin-right: 20px;
        ::v-deep {
          .el-cascader__tags {
            .el-tag {
              .el-tag__close {
                margin-top: 3px;
              }
            }
          }
        }
      }
      .save-btn {}
    }

    .export-btn {
      position: absolute;
      right: 24px;

      ::v-deep {
        .el-loading-mask {
          .el-loading-spinner {
            margin-top: -14px;
            .spinner {
              height: 26px;
            }
          }
        }
      }
    }

    .el-dropdown-link {
      margin-left: 10px;
      color: #0096cc;
      cursor: pointer;
    }
  }
}

.checkbox-check-all {
  margin: 5px 0;
  padding: 0 20px;
}

.config-checkbox-group {
  display: flex;
  flex-direction: column;
  padding: 0 20px;

  .config-checkbox-item {
    margin: 5px 0;
  }
}

.hr-filter-popper {
  .filter-node {
    display: flex;
    align-items: center;
    position: relative;
    padding: 0 16px 0 0;
    .node-name {
      max-width: 166px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
    }

    .node-count {
      display: block;
      margin: 0px 16px 0px 2px;
    }
    .delete-group {
      position: absolute;
      right: 0;
    }
  }
}
</style>
